import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/EnergyBreakdownListing.css";

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (val, decimals = 2) =>
  val !== null && val !== undefined && val !== "" && !isNaN(Number(val))
    ? Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "—";

const fmtPct = (val) =>
  val !== null && val !== undefined && !isNaN(Number(val))
    ? `${Number(val).toFixed(1)}%`
    : "—";

// ── Returns true if a summary card value is considered "empty" ───────────────
const isEmptyValue = (val) => {
  if (val === null || val === undefined) return true;
  const str = String(val).trim();
  if (str === "" || str === "—" || str === "-" || str.toUpperCase() === "NA") return true;
  const num = Number(val);
  if (!isNaN(num) && num === 0) return true;
  return false;
};

// ── Bar color based on percentage ────────────────────────────────────────────
// 100%        → #198754 (green)
// 50% – <100% → #111827 (dark)
// 0% – <50%   → #DC3545 (red)
const barColor = (pct) => {
  const n = Number(pct) || 0;
  if (n >= 100) return "#198754";
  if (n >= 50)  return "#111827";
  return "#DC3545";
};

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconZap = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);

const IconFuel = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" x2="15" y1="22" y2="22" />
    <line x1="4" x2="14" y1="9" y2="9" />
    <path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18" />
    <path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5" />
  </svg>
);

const IconElec = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
  </svg>
);

const IconLeaf = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
  </svg>
);

const IconDownload = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" x2="12" y1="15" y2="3" />
  </svg>
);

// ── Scope info ────────────────────────────────────────────────────────────────
const getScopeInfo = (scope) => {
  if (scope === "scope1") return { label: "Fuel",        icon: <IconFuel /> };
  if (scope === "scope2") return { label: "Electricity", icon: <IconElec /> };
  return                         { label: "Other",       icon: <IconElec /> };
};

const parseEnergyString = (str) => {
  if (!str) return { value: "—", unit: "" };
  const parts = String(str).trim().split(/\s+/);
  if (parts.length >= 2) return { value: parts[0], unit: parts.slice(1).join(" ") };
  return { value: parts[0], unit: "" };
};

// ── Build dynamic summary cards from API summary object ──────────────────────
// Each card: { label, value, unit, percent, icon, color }
// Cards with empty/zero values are filtered out.
const buildSummaryCards = (summary, totalEnergyUnit) => {
  const unit = totalEnergyUnit || "GJ";
  const candidates = [
    {
      label:   summary.electricity_title   ?? "Electricity",
      value:   summary.electricity_total_energy_gj,
      unit,
      percent: summary.electricity_share_percent,
      icon:    <IconElec />,
      color:   "#2563eb",
    },
    {
      label:   summary.scope1_title ?? "Fuel",
      value:   summary.scope1_total_energy_gj,
      unit,
      percent: summary.scope1_energy_share_percent,
      icon:    <IconFuel />,
      color:   "#16a34a",
    },
    {
      label:   summary.renewable_title ?? "Renewable",
      value:   summary.renewable_total_energy_gj,
      unit,
      percent: summary.renewable_share_percent,
      icon:    <IconLeaf />,
      color:   "#d97706",
    },
  ];
  // Filter out cards whose value is 0, null, undefined, "—", "-", "NA"
  return candidates.filter((c) => !isEmptyValue(c.value));
};

// ─────────────────────────────────────────────────────────────────────────────
const EnergyBreakdownListing = () => {
  const { reportingYearId } = useReportingYear();
  const location = useLocation();
  const navigate = useNavigate();

  const { title, entityId } = location.state || {};

  const [apiData,      setApiData]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (index) =>
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));

  useEffect(() => {
    if (!reportingYearId) return;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get("/api/energy_breakdown-data", {
          params: { reporting_year_id: reportingYearId, legal_entity_id: entityId },
        });
        if (res.data.success) setApiData(res.data);
        else setError(res.data.message || "Failed to load data.");
      } catch { setError("An error occurred while fetching data."); }
      finally  { setLoading(false); }
    };
    fetchData();
  }, [reportingYearId, entityId]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const rows    = apiData?.data    ?? [];
  const summary = apiData?.summary ?? {};

  const { value: totalEnergyVal, unit: totalEnergyUnit } = parseEnergyString(apiData?.total_energy_gj);

  // Dynamic summary cards — only those with non-zero values
  const summaryCards = buildSummaryCards(summary, totalEnergyUnit);

  // Bar rows — one per activity type
  const barRows = rows.map((item) => {
    const scopeInfo = getScopeInfo(item.scope);
    return {
      source:     item.activity_type ?? "—",
      energyVal:  item.total_energy_gj,
      energyUnit: totalEnergyUnit ?? "GJ",
      percentage: Number(item.energy_share_percent) || 0,
      scope:      item.scope,
      scopeLabel: scopeInfo.label,
      scopeIcon:  scopeInfo.icon,
    };
  });

  // ── CSV download ──────────────────────────────────────────────────────────
  const handleDownload = () => {
    const csvRows = [
      ["Source", "Category", "Quantity", "Unit", `Energy (${totalEnergyUnit || "GJ"})`, "% Share"],
      ...rows.map((r) => [
        r.activity_type,
        getScopeInfo(r.scope).label,
        r.total_consumption,
        r.unit_name,
        r.total_energy_gj,
        r.energy_share_percent,
      ]),
    ];
    const csv  = csvRows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = "energy_breakdown.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (loading) return (
    <div className="dash-page">
      <div className="ebl-loading">Loading…</div>
    </div>
  );
  if (error) return (
    <div className="dash-page">
      <div className="ebl-error">{error}</div>
    </div>
  );
  if (!apiData) return (
    <div className="dash-page">
      <div className="ebl-empty">No data available.</div>
    </div>
  );

  return (
    <div className="dash-page">

      {/* ══ HERO card — compact ═══════════════════════════════════════════ */}
      <div className="le-card ebl-hero">
          <div className="ebl-hero__icon-wrap">
            <IconZap />
          </div>
          <div className="ebl-hero__body">
            <div className="ebl-hero__title-row">
              <span className="ebl-hero__title">{title || "Total Energy Breakdown"}</span>
            </div>
            <div className="ebl-hero__subtitle">
              Source-wise energy consumption — normalised to {totalEnergyUnit || "GJ"}
            </div>
            <div className="ebl-hero__value">
              {fmt(totalEnergyVal)}
              <span className="ebl-hero__unit">{totalEnergyUnit || "GJ"}</span>
            </div>
          </div>
        </div>

      {/* ══ Summary cards — dynamic, skip zero/empty ══════════════════════ */}
     {summaryCards.length > 0 && (
          <div className="ebl-summary-grid">
            {summaryCards.map((c, i) => (
              <div key={i} className="le-card ebl-summary-card">
                {/* Content remains same as your original file */}
                <div className="ebl-summary-card__header">
                  <span className="ebl-summary-card__icon" style={{ color: c.color, background: `${c.color}18` }}>
                    {c.icon}
                  </span>
                  <span className="ebl-summary-card__label">{c.label}</span>
                </div>
                <div className="ebl-summary-card__value">{fmt(c.value)}</div>
                <div className="ebl-summary-card__meta">
                  {c.unit} · {fmtPct(c.percent)} of total
                </div>
              </div>
            ))}
          </div>
        )}

      {/* ══ Source Breakdown table ═════════════════════════════════════════ */}
      <div className="le-card ebl-table-card">
        <div className="ebl-section-title">Source Breakdown</div>
        <div className="ebl-table-wrap">
          <table className="ebl-table">
            <thead>
              <tr>
                <th style={{ textAlign: "left",  width: "20%" }}>Source</th>
                <th style={{ textAlign: "left",  width: "12%" }}>Category</th>
                <th style={{ textAlign: "right", width: "10%" }}>Quantity</th>
                <th style={{ textAlign: "left",  width: "10%" }}>Unit</th>
                <th style={{ textAlign: "right", width: "18%" }}>Conversion</th>
                <th style={{ textAlign: "right", width: "12%" }}>Energy ({totalEnergyUnit || "GJ"})</th>
                <th style={{ textAlign: "right", width: "8%"  }}>% Share</th>
                <th style={{ textAlign: "right", width: "10%" }}>Entries</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item, i) => {
                const scopeInfo    = getScopeInfo(item.scope);
                const isExpanded   = expandedRows[i];
                const hasSubEntries = item.entries && item.entries.length > 0;

                return (
                  <React.Fragment key={i}>
                    <tr className="ebl-table__row">
                      <td style={{ textAlign: "left", fontWeight: 500 }}>
                        {hasSubEntries && (
                          <button className="ebl-expand-btn" onClick={() => toggleRow(i)}>
                            {isExpanded ? "−" : "+"}
                          </button>
                        )}
                        {item.activity_type ?? "—"}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        <span className="ebl-category-badge">
                          <span className="ebl-category-badge__icon">{scopeInfo.icon}</span>
                          {scopeInfo.label}
                        </span>
                      </td>
                      <td style={{ textAlign: "right" }}>{fmt(item.total_consumption, 0)}</td>
                      <td style={{ textAlign: "left", color: "#6b7280" }}>{item.unit_name ?? "—"}</td>
                      <td style={{ textAlign: "right", color: "#6b7280", fontSize: 12 }}>—</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(item.total_energy_gj)}</td>
                      <td style={{ textAlign: "right" }}>
                        <span className="ebl-pct-badge">{fmtPct(item.energy_share_percent)}</span>
                      </td>
                      <td style={{ textAlign: "right", color: "#6b7280" }}>
                        {hasSubEntries ? item.entries.length : 0}
                      </td>
                    </tr>

                    {/* ── Expanded sub-entries ── */}
                    {isExpanded && hasSubEntries && (
                      <tr>
                        <td colSpan={8} className="ebl-expanded-container-td">
                          <div className="ebl-nested-card">
                            <div className="ebl-nested-header">
                              Source-level entries for <strong>{item.activity_type}</strong>
                            </div>
                            <table className="ebl-nested-table">
                              <thead>
                                <tr>
                                  <th>Source</th>
                                  <th>Date</th>
                                  <th>Location</th>
                                  <th>Department</th>
                                  <th style={{ textAlign: "right" }}>Quantity</th>
                                  <th>Unit</th>
                                  <th style={{ textAlign: "right" }}>Energy ({totalEnergyUnit || "GJ"})</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.entries.map((sub, si) => (
                                  <tr key={si}>
                                    <td>{sub.source_name ?? "—"}</td>
                                    <td>{sub.date ?? "—"}</td>
                                    <td>{sub.site_name ?? "—"}</td>
                                    <td>{sub.dept_name ?? "—"}</td>
                                    <td style={{ textAlign: "right" }}>{fmt(sub.con_value, 0)}</td>
                                    <td>{item.unit_name || "—"}</td>
                                    <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(sub.energy_gj)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ Contribution bars ═════════════════════════════════════════════ */}
      {barRows.length > 0 && (
        <div className="le-card ebl-bars-card">
          <div className="ebl-section-title">Contribution by Source</div>
          <div className="ebl-bars">
            {barRows.map((g, i) => {
              const fillColor = barColor(g.percentage);
              return (
                <div key={i} className="ebl-bar-block">
                  <div className="ebl-bar-header">
                    <div className="ebl-bar-label">
                      <span className="ebl-bar-scope-icon">{g.scopeIcon}</span>
                      <span>{g.source}</span>
                    </div>
                    <div className="ebl-bar-meta">
                      {fmt(g.energyVal)} {g.energyUnit} · <strong>{fmtPct(g.percentage)}</strong>
                    </div>
                  </div>
                  <div className="ebl-bar-track">
                    <div
                      className="ebl-bar-fill"
                      style={{
                        width:      `${Math.min(Math.max(g.percentage, 0), 100)}%`,
                        background: fillColor,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══ Calculation Logic ═════════════════════════════════════════════ */}
      <div className="le-card ebl-calc-card">
        <div className="ebl-section-title">Calculation Logic</div>
        <div className="ebl-calc-formula">
          Total Energy ({totalEnergyUnit || "GJ"}) = Σ (Quantity × Conversion Factor)
        </div>
        <ul className="ebl-calc-notes">
          <li>Electricity: kWh × 0.0036, MWh × 3.6, TJ × 1000</li>
          <li>Fuels use Net Calorific Value (NCV): Coal 24, Natural Gas 39, Diesel 38.42, Furnace Oil 40.4, LPG 47.3, Petrol 34.2, Biomass 15 {totalEnergyUnit || "GJ"}/unit</li>
          <li>Solar, Wind and Biomass are classified as Renewable energy</li>
        </ul>
      </div>

      {/* ══ Footer ════════════════════════════════════════════════════════ */}
      <div className="ebl-footer-actions">
        <button className="ebl-back-btn" onClick={() => navigate("/dashboard/environmental-summary")}>
          ← Back to Environment Summary
        </button>
        <button className="ebl-btn ebl-btn--solid" onClick={handleDownload}>
          <IconDownload />
          Download CSV
        </button>
      </div>

    </div>
  );
};

export default EnergyBreakdownListing;
